import type { OrganizationItem, OrganizationsListParams, OrganizationMember, DeletedOrganizationItem } from '@/types';

import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { createPaginationParams, createTableQueryOptions } from '@/components/table';
import { apiClient } from '@/utils/api';
import { buildApiParams } from '@/utils/helpers';

// Constants
const ORGANIZATIONS_BASE = '/organization';

// Types
type OrganizationsResponse = {
  success: boolean;
  message: string;
  data: OrganizationItem[];
  meta: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
};

type OrganizationMembersResponse = {
  success: boolean;
  data: OrganizationMember[];
  meta: { code: number };
};

// Types for deleted organizations
export type DeletedOrganizationsListParams = {
  page: number;
  per_page: number;
  search?: string;
  filters?: Record<string, string | undefined>;
  refresh?: number;
};

// Atoms
export const organizationsListParamsAtom = atom<OrganizationsListParams>({
  page: 1,
  per_page: createPaginationParams().per_page,
  filters: {}
});

export const deletedOrganizationsListParamsAtom = atom<DeletedOrganizationsListParams>({
  ...createPaginationParams(),
  filters: {},
});

export const organizationsHydratedAtom = atom<boolean>(false);

// Separate atom for autocomplete (always enabled)
export const organizationsAutocompleteQueryAtom = atomWithQuery((get) => {
  const params = get(organizationsListParamsAtom);

  return createTableQueryOptions({
    queryKey: ['organizations-autocomplete', params] as const,
    queryFn: () => {
      const apiParams = buildApiParams(
        {
          page: params.page,
          per_page: params.per_page
        },
        params.filters
      );

      return fetchOrganizations(apiParams);
    },
    enabled: true // Always enabled for autocomplete
  });
});

// Shared API function
const fetchOrganizations = async (params?: Record<string, any>): Promise<OrganizationsResponse> => {
  const { data } = await apiClient.get<OrganizationsResponse>(ORGANIZATIONS_BASE, { params });
  return data;
};

// Main query atom
export const organizationsQueryAtom = atomWithQuery((get) => {
  const params = get(organizationsListParamsAtom);
  const hydrated = get(organizationsHydratedAtom);

  return createTableQueryOptions({
    queryKey: ['organizations', params] as const,
    queryFn: () => {
      const apiParams = buildApiParams(
        {
          page: params.page,
          per_page: params.per_page
        },
        params.filters
      );

      return fetchOrganizations(apiParams);
    },
    enabled: hydrated,
  });
});

// Deleted organizations query atom
export const deletedOrganizationsQueryAtom = atomWithQuery<{
  success: boolean;
  message: string;
  data: DeletedOrganizationItem[];
  meta: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
}>((get) => {
  const params = get(deletedOrganizationsListParamsAtom);

  return createTableQueryOptions({
    queryKey: ["deleted-organizations", params] as const,
    queryFn: async () => {
      // Build params manually to avoid filter[] wrapper
      const apiParams: Record<string, any> = {};

      // Add pagination params
      if (params.page && params.page !== 1) {
        apiParams.page = params.page;
      }
      if (params.per_page && params.per_page !== 15) {
        apiParams.per_page = params.per_page;
      }

      // Add search filter
      if (params.search) {
        apiParams['filter[search]'] = params.search;
      }

      // Add filters
      if (params.filters) {
        Object.entries(params.filters).forEach(([key, value]) => {
          if (value) {
            apiParams[`filter[${key}]`] = value;
          }
        });
      }

      // Add only_trashed directly (not as filter)
      apiParams.only_trashed = 'true';

      const { data } = await apiClient.get(ORGANIZATIONS_BASE, { params: apiParams });

      // Return full typed response
      return data as {
        success: boolean;
        message: string;
        data: DeletedOrganizationItem[];
        meta: {
          total: number;
          per_page: number;
          current_page: number;
          last_page: number;
        };
      };
    }
  });
});

// API functions
export const updateOrganizationAtom = atom(
  null,
  async (get, set, { id, formData }: { id: number; formData: { name: string; desc: string; status: string; ai_daily_request_limit?: number | null } }) => {
    await apiClient.put(`${ORGANIZATIONS_BASE}/${id}`, formData);
    const currentParams = get(organizationsListParamsAtom);
    set(organizationsListParamsAtom, { ...currentParams, refresh: Date.now() });
  }
);

export const updateOrganization = async (id: number, data: Partial<OrganizationItem>) => {
  const { data: response } = await apiClient.put(`${ORGANIZATIONS_BASE}/${id}`, data);
  return response;
};

export const deleteOrganization = async (id: number) => {
  const { data: response } = await apiClient.delete(`${ORGANIZATIONS_BASE}/${id}`);
  return response;
};

// Add Organization Member
export const addOrganizationMember = atom(
  null,
  async (get, set, { organizationId, email, role }: { organizationId: number; email: string; role: string }) => {
    await apiClient.post(`/organization/${organizationId}/members`, {
      email,
      role
    });

    // Trigger refresh by updating params
    const currentParams = get(organizationsListParamsAtom);
    set(organizationsListParamsAtom, { ...currentParams, refresh: Date.now() });
  }
);

// Add Organization Domain
export const addOrganizationDomain = atom(
  null,
  async (
    get,
    set,
    {
      organizationId,
      domain,
      title,
      token_limit
    }: { organizationId: number; domain: string; title: string; token_limit?: number | null }
  ) => {
    const payload: Record<string, string | number | null | undefined> = { domain, title };
    if (token_limit !== undefined) {
      payload.token_limit = typeof token_limit === 'number' ? token_limit : null;
    }
    await apiClient.post(`/organization/${organizationId}/domains`, payload);

    // Trigger refresh by updating params
    const currentParams = get(organizationsListParamsAtom);
    set(organizationsListParamsAtom, { ...currentParams, refresh: Date.now() });
  }
);

// Remove Organization Member
export const removeOrganizationMember = atom(
  null,
  async (get, set, { organizationId, memberId }: { organizationId: number; memberId: number }) => {
    await apiClient.delete(`/organization/${organizationId}/members/${memberId}`);

    // Trigger refresh by updating params
    const currentParams = get(organizationsListParamsAtom);
    set(organizationsListParamsAtom, { ...currentParams, refresh: Date.now() });
  }
);

export const getOrganizationMembers = async (id: number): Promise<OrganizationMember[]> => {
  const { data: response } = await apiClient.get<OrganizationMembersResponse>(
    `${ORGANIZATIONS_BASE}/${id}/members`
  );
  return response.data;
};

// Fetch Organization Domains (as an atomized API service)
export const fetchOrganizationDomains = atom(
  null,
  async (get, set, { organizationId }: { organizationId: number }) => {
    const { data } = await apiClient.get<{ success: boolean; data: any[] }>(
      `${ORGANIZATIONS_BASE}/${organizationId}/domains`
    );
    return data?.data || [];
  }
);

// Restore Organization
export const restoreOrganization = async (id: number) => {
  const { data: response } = await apiClient.post(`${ORGANIZATIONS_BASE}/${id}/restore`);
  return response;
};

// Adjust Organization Tokens (add or deduct)
export type AdjustTokensPayload = {
  amount: number; // Positive = add/grant, negative = deduct/revoke
  description?: string | null;
};

export const adjustOrganizationTokens = async (
  organizationId: number,
  payload: AdjustTokensPayload
) => {
  const { data } = await apiClient.post(
    `${ORGANIZATIONS_BASE}/${organizationId}/adjust-tokens`,
    payload
  );
  return data;
};
